import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useGenUiConfig } from '../../shared/ui/config.js';
import { useGenUiI18n } from '../../shared/ui/i18n.js';
import type { ConsultPlacesBlock, PlaceDetails } from '@voll-intelligence/genui-contract';

type LoadState =
  | { status: 'loading' }
  | { status: 'loaded'; place: PlaceDetails }
  | { status: 'error' };

/** Separador da chave de carga; um place id do Google nunca o contém. */
const IDS_SEPARATOR = '\u001f';

export function useConsultPlaces(ui: ConsultPlacesBlock) {
  const { googleMapsApiKey, ports } = useGenUiConfig();
  const { locale } = useGenUiI18n();
  const placePort = ports?.places;
  const [states, setStates] = useState<Record<string, LoadState>>({});
  const [selectedId, setSelectedId] = useState<string>();
  /** O lugar em DESTAQUE no trilho (pin tocado ou ficha aberta) — navegação,
   *  não ação: a ficha só abre por `openDetails`. */
  const [focusedId, setFocusedId] = useState<string>();
  const [full, setFull] = useState<LoadState>();
  const [mapExpanded, setMapExpanded] = useState(false);
  const openerRef = useRef<HTMLButtonElement>(null);
  const summaryRequestsRef = useRef<Record<string, number>>({});
  const detailRequestRef = useRef(0);
  /** Ids já pedidos nesta (locale, porta) — carregados, em voo ou em erro. */
  const requestedRef = useRef({ locale, placePort, ids: new Set<string>() });
  /** Desarma toda resposta em voo (ficha e resumos): o que chegar depois cai fora. */
  const dropInFlight = useCallback(() => {
    detailRequestRef.current += 1;
    for (const id of Object.keys(summaryRequestsRef.current)) {
      summaryRequestsRef.current[id] = (summaryRequestsRef.current[id] ?? 0) + 1;
    }
  }, []);

  const loadSummary = useCallback(
    (placeId: string) => {
      const request = (summaryRequestsRef.current[placeId] ?? 0) + 1;
      summaryRequestsRef.current[placeId] = request;
      if (!placePort) {
        setStates((old) => ({ ...old, [placeId]: { status: 'error' } }));
        return;
      }
      setStates((old) => ({ ...old, [placeId]: { status: 'loading' } }));
      void placePort
        .getPlace(placeId, 'summary', locale)
        .then((place) => {
          if (summaryRequestsRef.current[placeId] === request)
            setStates((old) => ({ ...old, [placeId]: { status: 'loaded', place } }));
        })
        .catch(() => {
          if (summaryRequestsRef.current[placeId] === request)
            setStates((old) => ({ ...old, [placeId]: { status: 'error' } }));
        });
    },
    [locale, placePort],
  );

  /** A carga é por CONTEÚDO da lista, não por identidade do `ui`: o shell
   *  re-parseia o bloco a cada render (prod 18/09/2026, #3215 — cada re-render do
   *  `ChatConversation` refazia TODOS os GET /places, 13 lugares × ~2/s durante
   *  um turno). A chave serializa âncora + ids; o efeito só roda quando ela muda
   *  e só pede o que ainda não foi pedido nesta (locale, porta). */
  const idsKey = [ui.anchorPlaceId ?? '', ...ui.placeIds].join(IDS_SEPARATOR);
  // Outra lista (ou locale/porta novos): a ficha e o destaque saem no próprio render.
  const [shownFor, setShownFor] = useState({ idsKey, locale, placePort });
  if (
    shownFor.idsKey !== idsKey ||
    shownFor.locale !== locale ||
    shownFor.placePort !== placePort
  ) {
    setShownFor({ idsKey, locale, placePort });
    setSelectedId(undefined);
    setFocusedId(undefined);
    setFull(undefined);
  }
  useEffect(() => {
    detailRequestRef.current += 1;
    // Locale/porta novos: o que estava em voo cai fora e tudo volta a ser pedido.
    const requested = requestedRef.current;
    if (requested.locale !== locale || requested.placePort !== placePort) {
      dropInFlight();
      requestedRef.current = { locale, placePort, ids: new Set() };
    }
    const [anchorPlaceId, ...placeIds] = idsKey.split(IDS_SEPARATOR);
    const ids = new Set(placeIds);
    if (anchorPlaceId) ids.add(anchorPlaceId);
    for (const placeId of ids) {
      if (requestedRef.current.ids.has(placeId)) continue;
      requestedRef.current.ids.add(placeId);
      loadSummary(placeId);
    }
  }, [loadSummary, idsKey, locale, placePort, dropInFlight]);

  const loaded = useMemo(
    () =>
      ui.placeIds.flatMap((id, index) => {
        const state = states[id];
        return state?.status === 'loaded' ? [{ place: state.place, position: index + 1 }] : [];
      }),
    [states, ui.placeIds],
  );
  const anchorState = ui.anchorPlaceId ? states[ui.anchorPlaceId] : undefined;
  const anchorPlace = anchorState?.status === 'loaded' ? anchorState.place : undefined;

  const focusPlace = useCallback((placeId: string) => {
    setFocusedId(placeId);
  }, []);

  const openDetails = useCallback(
    (placeId: string, opener?: HTMLButtonElement) => {
      if (opener) openerRef.current = opener;
      setFocusedId(placeId);
      setSelectedId(placeId);
      const request = ++detailRequestRef.current;
      const summary = states[placeId];
      if (!placePort || summary?.status !== 'loaded') return;
      setFull({ status: 'loading' });
      void placePort
        .getPlace(placeId, 'full', locale)
        .then((place) => {
          if (detailRequestRef.current === request) setFull({ status: 'loaded', place });
        })
        .catch(() => {
          if (detailRequestRef.current === request) setFull({ status: 'error' });
        });
    },
    [locale, placePort, states],
  );

  const close = useCallback(() => {
    detailRequestRef.current += 1;
    setSelectedId(undefined);
    setFull(undefined);
    requestAnimationFrame(() => openerRef.current?.focus());
  }, []);
  // Desmontado: nenhuma resposta em voo escreve mais, e uma remontagem (StrictMode)
  // volta a pedir tudo pelo efeito de carga.
  useEffect(
    () => () => {
      dropInFlight();
      requestedRef.current.ids.clear();
    },
    [dropInFlight],
  );
  const selectedSummary = selectedId ? states[selectedId] : undefined;
  const sheetPlace =
    full?.status === 'loaded'
      ? full.place
      : selectedSummary?.status === 'loaded'
        ? selectedSummary.place
        : undefined;

  return {
    googleMapsApiKey,
    mapLoading:
      Boolean(googleMapsApiKey) &&
      ui.placeIds.some((id) => !states[id] || states[id].status === 'loading'),
    placePort,
    states,
    selectedId,
    focusedId,
    focusPlace,
    full,
    mapExpanded,
    setMapExpanded,
    loadSummary,
    loaded,
    anchorPlace,
    openDetails,
    close,
    sheetPlace,
  };
}
